import React, { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import FormFieldError from '../formFieldError';
import * as AppConstants from '../../utilities/constants';


interface IProps {
    label?: string;
    type: string;
    fieldName: string;
    control: any;
    errors: any;
    isRequired: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: any;
    handleChange?: any;
    placeholder?: string;
    disable?: boolean;
    trailingIconOffset?: number;
}

const SecureInputField: React.FC<IProps> = ({
    label,
    type,
    fieldName,
    control,
    errors,
    isRequired,
    maxLength,
    minLength,
    pattern,
    handleChange,
    placeholder,
    disable,
    trailingIconOffset = 36
}) => {
    const [isReadOnly, setIsReadOnly] = useState(true);

    const randomSuffix = useRef(
        `f${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
    ).current;

    const obfuscatedId = `f-${randomSuffix}`;
    const obfuscatedName = `field-${randomSuffix}`;

    const isMasked = type === 'password';

    const maskFontStyle: React.CSSProperties = {
        fontFamily: 'monospace',
        fontSize: '10px',
        letterSpacing: '1px'
    };

    return (
        <>
            {label && (
                <label className="block text-sm font-medium mb-1" htmlFor={obfuscatedId}>
                    {label} {isRequired ? <span className="text-red-500">*</span> : ''}
                </label>
            )}
            <Controller
                name={fieldName}
                control={control}
                rules={{ required: isRequired, maxLength, minLength, pattern }}
                render={({ field }) => {
                    const displayValue: string = field.value ?? '';
                    return (
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                className={'input-style'}
                                id={obfuscatedId}
                                name={obfuscatedName}
                                placeholder={isMasked ? undefined : placeholder}
                                disabled={disable}
                                style={
                                    isMasked
                                        ? { color: 'transparent', caretColor: '#111827', ...maskFontStyle }
                                        : undefined
                                }
                                autoComplete="new-password"
                                spellCheck={false}
                                autoCorrect="off"
                                autoCapitalize="none"
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                data-dashlane-ignore="true"
                                data-protonpass-ignore="true"
                                readOnly={isReadOnly}
                                onFocus={() => setIsReadOnly(false)}
                                onMouseDown={() => setIsReadOnly(false)}
                                onBlur={(e) => {
                                    setIsReadOnly(true);
                                    field.onBlur();
                                }}
                                value={displayValue}
                                onChange={(e) => {
                                    field.onChange(e);
                                    handleChange && handleChange(e);
                                }}
                                ref={field.ref}
                            />
                            {isMasked && (
                                <span
                                    aria-hidden="true"
                                    className="input-style"
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        pointerEvents: 'none',
                                        background: 'transparent',
                                        border: 'none',
                                        boxShadow: 'none',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        paddingRight: trailingIconOffset,
                                        WebkitMaskImage:
                                            'linear-gradient(to right, black calc(100% - 20px), transparent 100%)',
                                        maskImage:
                                            'linear-gradient(to right, black calc(100% - 20px), transparent 100%)',
                                        ...maskFontStyle
                                    }}
                                >
                                    {displayValue.length > 0 ? '•'.repeat(displayValue.length) : placeholder}
                                </span>
                            )}
                        </div>
                    );
                }}
            />
            {errors?.[fieldName]?.type === 'required' && (
                <FormFieldError message={AppConstants.requiredField} />
            )}
            {errors?.[fieldName]?.type === 'maxLength' && (
                <FormFieldError message={AppConstants.maxLengthReached} />
            )}
            {errors?.[fieldName]?.type === 'pattern' && (
                <FormFieldError message={AppConstants.notValidNumber} />
            )}
        </>
    );
};

export default SecureInputField;